import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";

import { Turnstile } from "@/components/forms/Turnstile";
import { getTrackingParams } from "@/lib/utm-tracking";

export const Route = createFileRoute("/test")({
  head: () => ({
    meta: [
      { title: "Web-to-Lead Test Form — RESTORE Hair" },
      {
        name: "description",
        content:
          "Internal sandbox page for testing the Salesforce Web-to-Lead form submission fields.",
      },
      { property: "og:title", content: "Web-to-Lead Test Form — RESTORE Hair" },
      {
        property: "og:description",
        content: "Internal sandbox for Salesforce Web-to-Lead field testing.",
      },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: TestPage,
});

const ORG_ID = "00DA0000000ZhkR";
const ENDPOINT = `https://webto.salesforce.com/servlet/servlet.WebToLead?encoding=UTF-8&orgId=${ORG_ID}`;
const RETURN_URL = "https://www.restorehair.com";

/** Salesforce custom-field IDs from the RESTORE production Web-to-Lead HTML. */
const F = {
  gbraid: "00NPC00000Cdhqz",
  wbraid: "00NPC00000CdhuD",
  gclid: "00N2K00000CJAv5",
  gclidAlt: "00N2K00000CCRxT",
  ipAddress: "00N2K00000CCRxW",
  leadSourceText: "00NA000000ARb9P",
  acceptedTerms: "00NA000000BMctX",
  doNotSms: "00NA000000BUiuI",
  market: "00NA000000BMcst",
  howHeard: "00NA000000BMcsy",
  entryId: "00NA000000BMct8",
  norwood: "00N2K00000B7xpV",
  submitterIp: "00N2K00000B5AWb",
};

const LEAD_SOURCES = [
  "--None--",
  "Party",
  "Store",
  "Fundraiser",
  "Rebooking",
  "Rep",
  "Employee Referral",
  "Radio Ad",
  "TV Ad",
  "Website",
  "Other",
  "Telemarketer",
  "Radio Unknown",
  "Google Search- Website Unknown",
  "Referral- Referral",
  "5280 Magazine - Magazine 5280",
  "Instagram & Facebook - Social Media",
  "YouTube- Need to add as a SF option",
  "Past Patient - Past Patient",
  "Other - Unknown Source / Multiple sources",
];

const MARKETS = [
  "--None--",
  "Chicago",
  "New York",
  "Charlotte",
  "Orlando",
  "Dallas",
  "Denver",
  "National",
  "Thairapy",
];

const NORWOOD = [
  "--None--",
  "Class 2",
  "Class 3",
  "Class 4",
  "Class 5",
  "Class 6",
  "Class 7",
  "N/A",
];

const labelCls = "block text-sm font-semibold text-foreground";
const inputCls =
  "mt-1 w-full rounded border border-border bg-background px-3 py-2 text-sm text-foreground";

function Field({
  label,
  name,
  id,
  type = "text",
  maxLength,
  value,
  onChange,
  autoComplete,
}: {
  label: string;
  name: string;
  id?: string;
  type?: string;
  maxLength?: number;
  value?: string;
  onChange?: (v: string) => void;
  autoComplete?: string;
}) {
  return (
    <div>
      <label className={labelCls} htmlFor={id ?? name}>
        {label}
      </label>
      <input
        className={inputCls}
        id={id ?? name}
        name={name}
        type={type}
        maxLength={maxLength}
        autoComplete={autoComplete}
        {...(onChange
          ? { value: value ?? "", onChange: (e) => onChange(e.target.value) }
          : {})}
      />
    </div>
  );
}

function Select({
  label,
  name,
  options,
}: {
  label: string;
  name: string;
  options: string[];
}) {
  return (
    <div>
      <label className={labelCls} htmlFor={name}>
        {label}
      </label>
      <select className={inputCls} id={name} name={name} defaultValue="">
        {options.map((o) => (
          <option key={o} value={o === "--None--" ? "" : o}>
            {o}
          </option>
        ))}
      </select>
    </div>
  );
}

function Checkbox({ label, name, id }: { label: string; name: string; id: string }) {
  return (
    <div className="flex items-center gap-2 pt-6">
      <input type="checkbox" id={id} name={name} value="1" className="h-4 w-4" />
      <label className={labelCls} htmlFor={id}>
        {label}
      </label>
    </div>
  );
}

function TestPage() {
  const [token, setToken] = useState("");
  const formRef = useRef<HTMLFormElement>(null);
  const [tracking, setTracking] = useState<Record<string, string>>({});
  const [gbraid, setGbraid] = useState("");
  const [wbraid, setWbraid] = useState("");
  const [gclid, setGclid] = useState("");

  // Capture click IDs + UTMs from the URL (or previously stored values) and
  // push them into the Salesforce fields before submit.
  useEffect(() => {
    const params = getTrackingParams();
    setTracking(params);
    if (params.gbraid) setGbraid(params.gbraid);
    if (params.wbraid) setWbraid(params.wbraid);
    if (params.gclid) setGclid(params.gclid);
    const form = formRef.current;
    if (!form) return;
    const set = (name: string, value?: string) => {
      if (!value) return;
      const el = form.elements.namedItem(name) as HTMLInputElement | null;
      if (el && "value" in el) el.value = value;
    };
    set(F.gclidAlt, params.gclid);
    set("utm_summary", JSON.stringify(params));
  }, []);

  return (
    <main className="mx-auto max-w-3xl px-4 py-12">
      <h1 className="text-3xl font-bold text-foreground">
        Salesforce Web-to-Lead Test Form
      </h1>
      <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
        Internal test page. Posts directly to the Salesforce <strong>production</strong>{" "}
        Web-to-Lead endpoint (org <code>{ORG_ID}</code>). Submission is gated by
        Cloudflare Turnstile. All custom-field IDs are wired from the supplied
        production HTML.
      </p>

      <form
        ref={formRef}
        action={ENDPOINT}
        method="POST"
        className="mt-8 grid gap-5 sm:grid-cols-2"
      >
        <input type="hidden" name="oid" value={ORG_ID} />
        <input type="hidden" name="retURL" value={RETURN_URL} />
        <input type="hidden" name="utm_summary" />
        {(
          ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"] as const
        ).map((k) => (
          <input key={k} type="hidden" name={k} value={tracking[k] ?? ""} readOnly />
        ))}

        <Field label="First Name" name="first_name" maxLength={40} />
        <Field label="Last Name" name="last_name" maxLength={80} />
        <Field label="Email" name="email" type="email" maxLength={80} />
        <Field label="Company" name="company" maxLength={40} />
        <Field label="City" name="city" maxLength={40} />
        <Field label="State/Province" name="state" maxLength={20} />
        <Field label="Phone" name="phone" maxLength={40} />

        <Field
          label="GBRAID"
          name={F.gbraid}
          id="gbraid"
          maxLength={255}
          autoComplete="off"
          value={gbraid}
          onChange={setGbraid}
        />
        <Field
          label="WBRAID"
          name={F.wbraid}
          id="wbraid"
          maxLength={255}
          autoComplete="off"
          value={wbraid}
          onChange={setWbraid}
        />
        <Field
          label="GCLID"
          name={F.gclid}
          id="gclid"
          maxLength={100}
          autoComplete="off"
          value={gclid}
          onChange={setGclid}
        />
        <Checkbox label="Accepted Terms" name={F.acceptedTerms} id="accepted_terms" />
        <Checkbox label="Do Not SMS" name={F.doNotSms} id="do_not_sms" />

        <Select label="Lead Source" name="lead_source" options={LEAD_SOURCES} />
        <Select label="Market" name={F.market} options={MARKETS} />
        <div className="sm:col-span-2">
          <label className={labelCls} htmlFor="how_heard">
            How did you hear about RESTORE?
          </label>
          <textarea className={inputCls} id="how_heard" name={F.howHeard} rows={3} />
        </div>
        <Field label="Entry ID Website" name={F.entryId} id="entry_id" />
        <Select label="Norwood Class" name={F.norwood} options={NORWOOD} />
        <Field
          label="Submitter IP"
          name={F.submitterIp}
          id="submitter_ip"
          maxLength={250}
        />
        <Field label="GCLID (alt)" name={F.gclidAlt} id="gclid_alt" maxLength={255} />
        <Field label="IP Address" name={F.ipAddress} id="ip_address" maxLength={255} />
        <Field
          label="Lead Source (text)"
          name={F.leadSourceText}
          id="lead_source_text"
          maxLength={60}
        />

        <div className="sm:col-span-2">
          <p className={labelCls}>Bot check</p>
          <Turnstile action="web-to-lead-test" onToken={setToken} onExpired={() => setToken("")} onError={() => setToken("")} />
        </div>

        <div className="sm:col-span-2 rounded border border-border bg-muted/40 p-4">
          <p className={labelCls}>Captured tracking parameters (debug)</p>
          <pre className="mt-2 overflow-x-auto text-xs text-muted-foreground">
            {Object.keys(tracking).length
              ? JSON.stringify(tracking, null, 2)
              : "none — append ?gclid=…&gbraid=…&wbraid=…&utm_source=… to the URL"}
          </pre>
        </div>

        <div className="sm:col-span-2">
          <button
            type="submit"
            disabled={!token}
            className="bg-primary px-6 py-3 text-sm font-bold tracking-wider text-primary-foreground transition hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {token ? "Submit test lead" : "Complete the bot check"}
          </button>
        </div>
      </form>
    </main>
  );
}
